import React, { useState, useEffect } from 'react';
import axios from 'axios';
import RichTextEditor from './RichTextEditor';

const API = process.env.REACT_APP_BACKEND_URL;
const COLORS = { primary: '#2b3380', success: '#4caf50', warning: '#ff9800', error: '#f44336' };

function EmailComposer({ onClose, onSent, replyTo = null }) {
  const [to, setTo] = useState(replyTo ? [replyTo.user_email] : []);
  const [cc, setCc] = useState([]);
  const [bcc, setBcc] = useState([]);
  const [subject, setSubject] = useState(replyTo ? `Re: ${replyTo.subject}` : '');
  const [body, setBody] = useState('');
  const [showCc, setShowCc] = useState(false);
  const [showBcc, setShowBcc] = useState(false);
  const [sending, setSending] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [toInput, setToInput] = useState('');
  const [ccInput, setCcInput] = useState('');
  const [bccInput, setBccInput] = useState('');
  const [attachments, setAttachments] = useState([]);

  useEffect(() => {
    fetchEmployees();
    if (replyTo) {
      setBody(`<br><br><hr><p><strong>On ${new Date(replyTo.sent_at || replyTo.created_at).toLocaleString()}, ${replyTo.user_email} wrote:</strong></p>${replyTo.body}`);
    }
  }, []);

  const fetchEmployees = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/employees`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEmployees(response.data || []);
    } catch (error) {
      console.error('Error fetching employees:', error);
    }
  };

  const handleSend = async (isDraft = false) => {
    if (!isDraft && to.length === 0) {
      alert('Please add at least one recipient');
      return;
    }
    if (!isDraft && !subject.trim()) {
      alert('Please add a subject');
      return;
    }

    setSending(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/api/emails/compose`,
        {
          to,
          cc,
          bcc,
          subject,
          body,
          is_draft: isDraft
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      alert(isDraft ? 'Draft saved!' : 'Email sent successfully!');
      onSent();
      onClose();
    } catch (error) {
      console.error('Error sending email:', error);
      alert(error.response?.data?.detail || 'Failed to send email');
    } finally {
      setSending(false);
    }
  };

  const addEmail = (email, type) => {
    if (!email || !email.includes('@')) return;
    
    if (type === 'to' && !to.includes(email)) {
      setTo([...to, email]);
      setToInput('');
    } else if (type === 'cc' && !cc.includes(email)) {
      setCc([...cc, email]);
      setCcInput('');
    } else if (type === 'bcc' && !bcc.includes(email)) {
      setBcc([...bcc, email]);
      setBccInput('');
    }
  };

  const removeEmail = (email, type) => {
    if (type === 'to') setTo(to.filter(e => e !== email));
    else if (type === 'cc') setCc(cc.filter(e => e !== email));
    else if (type === 'bcc') setBcc(bcc.filter(e => e !== email));
  };

  const modules = {
    toolbar: [
      [{ 'header': [1, 2, 3, false] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ 'list': 'ordered'}, { 'list': 'bullet' }],
      [{ 'color': [] }, { 'background': [] }],
      ['link'],
      ['clean']
    ]
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg w-full max-w-4xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="p-4 border-b border-gray-200 flex justify-between items-center">
          <h2 className="text-xl font-bold" style={{ color: COLORS.primary }}>
            {replyTo ? 'Reply' : 'New Message'}
          </h2>
          <button onClick={onClose} className="text-2xl text-gray-500 hover:text-gray-700">×</button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-4">
          {/* To Field */}
          <div className="mb-3">
            <div className="flex items-center gap-2 mb-2">
              <label className="text-sm font-medium text-gray-700 w-12">To:</label>
              <div className="flex-1 flex flex-wrap gap-2 items-center">
                {to.map((email, idx) => (
                  <span key={idx} className="bg-blue-100 text-blue-800 px-2 py-1 rounded-full text-sm flex items-center gap-1">
                    {email}
                    <button onClick={() => removeEmail(email, 'to')} className="text-blue-600 hover:text-blue-800">×</button>
                  </span>
                ))}
                <input
                  type="email"
                  value={toInput}
                  onChange={(e) => setToInput(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && addEmail(toInput, 'to')}
                  onBlur={() => toInput && addEmail(toInput, 'to')}
                  placeholder="Add recipient..."
                  className="flex-1 min-w-[200px] px-2 py-1 border-none focus:outline-none"
                />
              </div>
              <div className="flex gap-2">
                <button onClick={() => setShowCc(!showCc)} className="text-sm text-blue-600 hover:underline">Cc</button>
                <button onClick={() => setShowBcc(!showBcc)} className="text-sm text-blue-600 hover:underline">Bcc</button>
              </div>
            </div>
          </div>

          {/* CC Field */}
          {showCc && (
            <div className="mb-3">
              <div className="flex items-center gap-2">
                <label className="text-sm font-medium text-gray-700 w-12">Cc:</label>
                <div className="flex-1 flex flex-wrap gap-2 items-center">
                  {cc.map((email, idx) => (
                    <span key={idx} className="bg-gray-100 text-gray-800 px-2 py-1 rounded-full text-sm flex items-center gap-1">
                      {email}
                      <button onClick={() => removeEmail(email, 'cc')} className="text-gray-600 hover:text-gray-800">×</button>
                    </span>
                  ))}
                  <input
                    type="email"
                    value={ccInput}
                    onChange={(e) => setCcInput(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && addEmail(ccInput, 'cc')}
                    onBlur={() => ccInput && addEmail(ccInput, 'cc')}
                    placeholder="Add Cc..."
                    className="flex-1 min-w-[200px] px-2 py-1 border-none focus:outline-none"
                  />
                </div>
              </div>
            </div>
          )}

          {/* BCC Field */}
          {showBcc && (
            <div className="mb-3">
              <div className="flex items-center gap-2">
                <label className="text-sm font-medium text-gray-700 w-12">Bcc:</label>
                <div className="flex-1 flex flex-wrap gap-2 items-center">
                  {bcc.map((email, idx) => (
                    <span key={idx} className="bg-gray-100 text-gray-800 px-2 py-1 rounded-full text-sm flex items-center gap-1">
                      {email}
                      <button onClick={() => removeEmail(email, 'bcc')} className="text-gray-600 hover:text-gray-800">×</button>
                    </span>
                  ))}
                  <input
                    type="email"
                    value={bccInput}
                    onChange={(e) => setBccInput(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && addEmail(bccInput, 'bcc')}
                    onBlur={() => bccInput && addEmail(bccInput, 'bcc')}
                    placeholder="Add Bcc..."
                    className="flex-1 min-w-[200px] px-2 py-1 border-none focus:outline-none"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Subject */}
          <div className="mb-3">
            <input
              type="text"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="Subject"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2"
              style={{ focusRingColor: COLORS.primary }}
            />
          </div>

          {/* Rich Text Editor */}
          <div className="mb-3">
            <RichTextEditor
              value={body}
              onChange={setBody}
              modules={modules}
              placeholder="Write your email..."
              style={{ height: '300px', marginBottom: '50px' }}
            />
          </div>

          {/* Attachments Display */}
          {attachments.length > 0 && (
            <div className="mb-3">
              <div className="text-sm font-medium text-gray-700 mb-2">Attachments:</div>
              <div className="flex flex-wrap gap-2">
                {attachments.map((file, idx) => (
                  <div key={idx} className="bg-gray-100 px-3 py-2 rounded-lg text-sm flex items-center gap-2">
                    <span>📎 {file.name} ({(file.size / 1024).toFixed(1)} KB)</span>
                    <button 
                      onClick={() => setAttachments(attachments.filter((_, i) => i !== idx))}
                      className="text-red-600 hover:text-red-800"
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-gray-200 flex justify-between items-center">
          <div className="flex gap-2">
            <button
              onClick={() => handleSend(true)}
              disabled={sending}
              className="px-4 py-2 bg-gray-200 rounded-lg hover:bg-gray-300"
            >
              💾 Save Draft
            </button>
            <label className="px-4 py-2 bg-gray-100 rounded-lg hover:bg-gray-200 cursor-pointer">
              📎 Attach
              <input 
                type="file" 
                className="hidden" 
                multiple
                onChange={(e) => {
                  const files = Array.from(e.target.files || []);
                  if (files.length > 0) {
                    setAttachments([...attachments, ...files]);
                  }
                }}
              />
            </label>
          </div>
          <div className="flex gap-2">
            <button
              onClick={onClose}
              className="px-4 py-2 bg-gray-200 rounded-lg hover:bg-gray-300"
            >
              Cancel
            </button>
            <button
              onClick={() => handleSend(false)}
              disabled={sending}
              className="px-6 py-2 text-white rounded-lg font-medium hover:opacity-90"
              style={{ backgroundColor: COLORS.primary }}
            >
              {sending ? 'Sending...' : '📧 Send'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default EmailComposer;
